import { format } from 'date-fns';
import { useRef, useState } from 'react';

import { useCurrentUser } from '@/hooks';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  TextField,
  Typography,
} from '@/ui';

import type { User } from '@/types';
import type React from 'react';
import type { FC } from 'react';

import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import { useExtendSessionsForUserMutation } from '@/store/api/sessionApi';
import { useAppDispatch } from '@/store/hooks';
import { setChatId } from '@/store/slices/appSlice';
import { isAdmin } from '@/types/user';

type EmptyProps = {
  client: User;
};

const Empty: FC<EmptyProps> = ({ client }) => {
  const dispatch = useAppDispatch();
  const { cometChatId, fullName, coaches = [] } = client;
  const { user } = useCurrentUser();
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendCount, setExtendCount] = useState<string>('20');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [copySuccessMessage, setCopySuccessMessage] = useState<string>('');
  const [copyErrorMessage, setCopyErrorMessage] = useState<string>('');
  const copyRequestId = useRef(0);
  const [extendSessions, { isLoading: isExtending }] =
    useExtendSessionsForUserMutation();

  const coachLinks = coaches
    .filter((coach) => {
      if (isAdmin(coach)) return false;
      if (usesMomentsScheduling(coach)) return true;
      return !!coach.coachProfile?.calendarUrl;
    })
    .map((coach) => {
      // Preserve both intents: main introduced the `usesMomentsScheduling`
      // helper (matches the filter above), and MEN-4677 needs firstName
      // for the button label.
      const { id: mappedId, coachProfile, firstName } = coach;
      const isMoments = usesMomentsScheduling(coach);
      const link = isMoments
        ? `${window.location.origin}/m/book-chemistry-call/${mappedId}`
        : `${import.meta.env.VITE_CAL_URL}/${coachProfile?.calendarUrl}`;
      // Both link forms above resolve to a chemistry-call booking on Moments
      // (the /m/book-chemistry-call/ route) or to the coach's Cal.com page
      // whose default booking event is the chemistry call. Bi-weekly booking
      // would require the member to have completed a chemistry call first
      // and would use a different link (/m/book-biweekly/); if we ever add
      // that here, this label must derive from per-coach session state.
      const label = firstName
        ? `Book Chemistry Call with ${firstName}`
        : 'Book Chemistry Call';
      return { id: mappedId, label, link };
    });

  const onMessage = () => dispatch(setChatId(cometChatId));
  const onCopySchedulingLink = (link: string) => async () => {
    const requestId = copyRequestId.current + 1;
    copyRequestId.current = requestId;
    try {
      await navigator.clipboard.writeText(link);
      if (requestId !== copyRequestId.current) return;
      setCopyErrorMessage('');
      setCopySuccessMessage('Chemistry call link copied');
    } catch {
      if (requestId !== copyRequestId.current) return;
      setCopySuccessMessage('');
      setCopyErrorMessage(
        'Unable to copy chemistry call link. Please try again.',
      );
    }
  };
  const onOpenExtend = () => {
    setErrorMessage('');
    setExtendOpen(true);
  };
  const onCloseExtend = () => {
    setExtendOpen(false);
    setErrorMessage('');
  };
  const onChangeCount = (e: React.ChangeEvent<HTMLInputElement>) =>
    setExtendCount(e.target.value);
  const onSubmitExtend = async () => {
    const n = parseInt(extendCount || '0', 10);
    if (!Number.isFinite(n) || n <= 0) return;
    try {
      const res = await extendSessions({
        extendByCount: n,
        userId: client.id,
      }).unwrap();
      const bookedThrough = res.booked_until
        ? format(new Date(res.booked_until), 'MMM d, yyyy')
        : '';
      setSuccessMessage(
        `${res.created} ${res.created === 1 ? 'session' : 'sessions'} booked out through ${bookedThrough}`,
      );
      onCloseExtend();
    } catch (err: unknown) {
      const { data } = err as { data?: unknown };
      let message = 'Failed to extend sessions';
      if (typeof data === 'string') {
        message = data;
      } else if (data && typeof data === 'object') {
        const rec = data as Record<string, unknown>;
        const e1 = rec.error;
        if (typeof e1 === 'string') {
          message = e1;
        } else {
          const e2 = rec.errors;
          if (Array.isArray(e2)) {
            message = e2.map((v) => String(v)).join('; ');
          }
        }
      }
      setErrorMessage(message);
    }
  };

  return (
    <>
      <div className="grid size-12 place-items-center rounded-full bg-sage-200">
        <Icon className="!text-2xl text-sage-600">calendar_add_on</Icon>
      </div>
      <div className="pt-4">
        <Typography className="mb-2" variant="h5">
          No upcoming sessions scheduled
        </Typography>
        <Typography className="mb-5 pr-20" variant="body2">
          Consider reaching out to encourage them to schedule their next
          session. You can copy and share your scheduling link with them below
          for your convenience.
        </Typography>
      </div>
      <div className="flex flex-wrap gap-2">
        {successMessage && (
          <Alert className="mr-auto" severity="success">
            {successMessage}
          </Alert>
        )}
        {copySuccessMessage && (
          <Alert className="mr-auto" severity="success">
            {copySuccessMessage}
          </Alert>
        )}
        {copyErrorMessage && (
          <Alert className="mr-auto" severity="error">
            {copyErrorMessage}
          </Alert>
        )}
        {!isAdmin(user) && (
          <Button variant="outlined" onClick={onMessage}>
            Message {fullName}
          </Button>
        )}
        {isAdmin(user) && (
          <Button variant="contained" onClick={onOpenExtend}>
            Extend sessions
          </Button>
        )}
        {coachLinks.map(({ id: linkId, label, link }) => (
          <Button
            key={`${linkId}-coach-links`}
            color="gray"
            endIcon={<Icon className="m-1">content_copy</Icon>}
            size="large"
            variant="contained"
            onClick={onCopySchedulingLink(link)}
          >
            <Typography variant="caption">{label}</Typography>
          </Button>
        ))}
      </div>

      <Dialog open={extendOpen} onClose={onCloseExtend}>
        <DialogTitle>Extend sessions</DialogTitle>
        <DialogContent>
          {errorMessage && (
            <Alert className="mb-3" severity="error">
              {errorMessage}
            </Alert>
          )}
          <Typography className="mb-3" variant="body2">
            How many additional sessions would you like to add?
          </Typography>
          <TextField
            inputMode="numeric"
            label="Count"
            margin="dense"
            type="number"
            value={extendCount}
            autoFocus
            fullWidth
            onChange={onChangeCount}
          />
        </DialogContent>
        <DialogActions>
          <Button color="gray" onClick={onCloseExtend}>
            Cancel
          </Button>
          <Button
            disabled={isExtending}
            variant="contained"
            onClick={onSubmitExtend}
          >
            Extend
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Empty;
